import { useCallback, useState, useEffect } from "react";
import { useUserContext } from "../../contexts/UserContext";
import { catchError } from "../../services/DaoService";
import { Button, Input, Spin, Progress } from "antd";
import { PlusCircleFilled, ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { DepenseService } from "../../services/DepenseService";
import DepenseCreateForm from "./DepenseCreateForm";
import DepenseList from "./DepenseList";
import PropTypes from "prop-types";
import styles from "./DepenseByAffaireList.module.scss";

export default function DepenseByAffaireList({ affaire, isReadOnly = true }) {
  const [paginationData, setPaginationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newModalVisible, setNewModalVisible] = useState(false);
  const { check } = useUserContext();
  const [searchText, setSearchText] = useState('');
  const [depenses, setDepenses] = useState([]);

  const init = useCallback(() => {
    setLoading(true);
    DepenseService.getInstance()
      .search({ searchText: searchText || null, affaire_id: affaire.id })
      .then(r => { setPaginationData(r.data); setDepenses(r.data.data); })
      .catch(catchError)
      .finally(() => setLoading(false));
  }, [searchText, affaire]);

  useEffect(() => { init(); }, [init, check]);

  const sym = affaire.monnaie?.symbole ?? '';
  const taux = affaire.taux_execution_budget ?? 0;
  const color = taux > 90 ? '#FF4D4F' : taux > 70 ? '#FAAD14' : '#52C41A';

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <span className={styles.sectionTitle}>Dépenses</span>
        <div className={styles.sectionActions}>
          <Button size="small" icon={<ReloadOutlined />} onClick={init} />
          {check('CREATE-DEPENSE') && !isReadOnly && (
            <Button size="small" type="primary" icon={<PlusCircleFilled />} onClick={() => setNewModalVisible(true)}>
              Ajouter
            </Button>
          )}
        </div>
      </div>

      <div className={styles.budgetChips}>
        <div className={styles.chip}>
          <span className={styles.chipLabel}>Budget</span>
          <span className={styles.chipValue}>{affaire.budget} {sym}</span>
        </div>
        <div className={styles.chipDivider} />
        <div className={styles.chip}>
          <span className={styles.chipLabel}>Dépensé</span>
          <span className={styles.chipValue}>{affaire.totale_depense} {sym}</span>
        </div>
        <div className={styles.chipDivider} />
        <div className={styles.chip}>
          <span className={styles.chipLabel}>Taux</span>
          <span className={styles.chipValue} style={{ color }}>{taux}%</span>
        </div>
        <div className={styles.progressBar}>
          <Progress percent={taux} showInfo={false} size={['100%', 4]} strokeColor={color} />
        </div>
      </div>

      <div className={styles.searchBar}>
        <Input
          prefix={<SearchOutlined style={{ color: '#8C9BAB' }} />}
          placeholder="Rechercher une dépense..."
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          size="small"
          allowClear
        />
      </div>

      <Spin spinning={loading}>
        {paginationData && (
          <DepenseList
            affaire={affaire}
            depenses={depenses}
            isReadOnly={isReadOnly}
            setDepenses={setDepenses}
            paginationData={paginationData}
            setPaginationData={setPaginationData}
            searchText={searchText}
          />
        )}
      </Spin>

      <DepenseCreateForm
        affaire={affaire}
        visible={newModalVisible}
        onCreate={init}
        onCancel={() => setNewModalVisible(false)}
      />
    </div>
  );
}

DepenseByAffaireList.propTypes = {
  affaire: PropTypes.object.isRequired,
  isReadOnly: PropTypes.bool,
};
